import { create } from 'zustand';
import { type Asset } from "@/entities/asset";

interface AssetStore {
    assets: Asset[];
    setAssets: (assets: Asset[]) => void;
    addAsset: (asset: Asset) => void;
    updateAsset: (id: string, asset: Partial<Asset>) => void;
    deleteAsset: (id: string) => void;
    getAssetsByTenant: (tenantId: string) => Asset[];
    getAssetsByBranch: (branchId: string) => Asset[];
    getAssetsByModel: (modelId: string) => Asset[];
    getAssetById: (id: string) => Asset | undefined;

    // UI State
    isAddAssetFormOpen: boolean;
    setIsAddAssetFormOpen: (isOpen: boolean) => void;
    editingAssetId: string | undefined;
    setEditingAssetId: (id: string | undefined) => void;
    /** When set, form opens in create mode prefilled from this asset (duplicate flow). */
    duplicateFromAssetId: string | null;
    setDuplicateFromAssetId: (id: string | null) => void;
    /** Create asset from asset model page: lock model (and category) to this id */
    lockedAssetModelId: string | null;
    setLockedAssetModelId: (id: string | null) => void;
    isDetailsOpen: boolean;
    setIsDetailsOpen: (isOpen: boolean) => void;
    selectedAsset: Asset | null;
    setSelectedAsset: (asset: Asset | null) => void;
    isImportOpen: boolean;
    setIsImportOpen: (isOpen: boolean) => void;
    isDeleteConfirmOpen: boolean;
    setIsDeleteConfirmOpen: (isOpen: boolean) => void;
    assetToDelete: Asset | null;
    setAssetToDelete: (asset: Asset | null) => void;
    
    // Filter State
    filters: {
        category: string;
        model: string;
        branch: string;
        labels: string[];
        labelLogic: 'AND' | 'OR';
        search: string;
    };
    setFilters: (filters: Partial<AssetStore['filters']>) => void;
    resetFilters: () => void;
}

export const useAssetStore = create<AssetStore>((set, get) => ({
    assets: [],
    setAssets: (assets) => set({ assets }),
    addAsset: (asset) => set((state) => ({ assets: [...state.assets, asset] })),
    updateAsset: (id, updatedAsset) =>
        set((state) => ({
            assets: state.assets.map((a) => (a.id === id ? { ...a, ...updatedAsset } : a)),
        })),
    deleteAsset: (id) => set((state) => ({ assets: state.assets.filter((a) => a.id !== id) })),
    getAssetsByTenant: (tenantId) =>
        get().assets.filter((a) => a.accountId === tenantId),
    getAssetsByBranch: (branchId) =>
        get().assets.filter((a) => a.location_id === branchId),
    getAssetsByModel: (modelId) => get().assets.filter((a) => a.asset_model_id === modelId),
    getAssetById: (id) => get().assets.find((a) => a.id === id),

    // UI State Implementation
    isAddAssetFormOpen: false,
    setIsAddAssetFormOpen: (isOpen) =>
        set(() => ({
            isAddAssetFormOpen: isOpen,
            ...(isOpen === false
                ? { editingAssetId: undefined, duplicateFromAssetId: null, lockedAssetModelId: null }
                : {}),
        })),
    editingAssetId: undefined,
    setEditingAssetId: (id) => set({ editingAssetId: id }),
    duplicateFromAssetId: null,
    setDuplicateFromAssetId: (id) => set({ duplicateFromAssetId: id }),
    lockedAssetModelId: null,
    setLockedAssetModelId: (id) => set({ lockedAssetModelId: id }),
    isDetailsOpen: false,
    setIsDetailsOpen: (isOpen) => set({ isDetailsOpen: isOpen }),
    selectedAsset: null,
    setSelectedAsset: (asset) => set({ selectedAsset: asset }),
    isImportOpen: false,
    setIsImportOpen: (isOpen) => set({ isImportOpen: isOpen }),
    isDeleteConfirmOpen: false,
    setIsDeleteConfirmOpen: (isOpen) => set({ isDeleteConfirmOpen: isOpen }),
    assetToDelete: null,
    setAssetToDelete: (asset) => set({ assetToDelete: asset }),

    // Filter State Implementation
    filters: {
        category: 'all',
        model: 'all',
        branch: 'all',
        labels: [],
        labelLogic: 'AND',
        search: '',
    },
    setFilters: (newFilters) =>
        set((state) => ({
            filters: { ...state.filters, ...newFilters },
        })),
    resetFilters: () =>
        set({
            filters: {
                category: 'all',
                model: 'all',
                branch: 'all',
                labels: [],
                labelLogic: 'AND',
                search: '',
            },
        }),
}));
